import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError, shortDate, TX_LABELS } from "../lib/api";
import { toast } from "sonner";
import { Plus, ArrowUp, ArrowDown, Trash, X } from "@phosphor-icons/react";
import MoneyInput from "../components/MoneyInput";

// Local-time "YYYY-MM-DDTHH:MM" for <input type="datetime-local"> default value.
// Using new Date().toISOString() returns UTC, which appears 1-2h behind local
// time in Italy (UTC+1/+2) and confuses users.
function localNowForInput() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Dynamic form configuration per transaction type
const TYPE_CONFIG = {
    entrata: {
        needs_to: true,
        hidden: true, // Deprecato: usa "Rettifica service" (owner) o "Accredito" con wallet
        description: "Incasso generico in un wallet (entrata cash/bonifico)",
    },
    uscita: {
        needs_from: true,
        needs_category: true,
        description: "Spesa o pagamento. Scegli la categoria per il P&L.",
    },
    trasferimento: {
        needs_from: true,
        needs_to: true,
        description: "Sposta denaro tra due wallet (es. Cassa → Cassaforte)",
    },
    vendita_credito: {
        needs_to: true,
        needs_provider: true,
        description:
            "Vendita manuale di credito scommesse al cliente in contanti (uso solo se non usi la sync residuo)",
    },
    rientro_fido: {
        needs_from: true,
        needs_provider: true,
        description:
            "Versamento al fornitore: cassa/banca -, outstanding -. Usa sia per il rientro fido settimanale sia per ricaricare un provider prepagato.",
    },
    scarico_plusvalenza: {
        needs_to: true,
        needs_provider: true,
        manager_only: true,
        description:
            "Opposto del versamento provider: il fornitore ti manda un bonifico in banca/cassa. Il wallet scelto sale, la posizione verso il provider aumenta (se era a credito verso di te, si riduce). NON impatta il P&L.",
    },
    accredito: {
        accredito_target: true, // choice esclusiva: fornitore XOR wallet
        description:
            "Accredito / entrata pura. Scegli SE è un accredito da un fornitore (outstanding scende) OPPURE un versamento direttamente su un wallet (es. cashback bancario, rimborso, regalo). In entrambi i casi conta come entrata nel P&L.",
    },
    sospeso: {
        needs_client: true,
        needs_provider: true,
        description:
            "Cliente gioca credito virtuale a debito: debito cliente + sale, fido provider consumato. La cassa NON cambia.",
    },
    rientro_sospeso: {
        needs_client: true,
        needs_to: true,
        description:
            "Il cliente paga (totalmente o parzialmente) il sospeso, oppure versa contanti/bonifico anche senza giocata: cassa/banca +, saldo cliente -. Se il versamento supera il debito, il saldo del cliente va a credito (l'agenzia gli deve indietro la differenza).",
    },
    prelievo_utili: {
        needs_utili: true,
        needs_from: true,
        description:
            "Un socio preleva utili accantonati: scarica il wallet utili e toglie dalla cassa",
    },
    incasso_pos: {
        needs_provider: true,
        needs_pos_device: true,
        description:
            "Incasso POS: banca riceve subito il NETTO (lordo − commissione), il fido provider scende del lordo, la commissione va già a P&L come spesa. Resta in 'Pending' finché non confermi l'arrivo del bonifico in banca (a quel punto basta un click, niente nuove tx).",
    },
    apporto_capitale: {
        needs_to: true,
        description:
            "Conferimento di capitale dei soci: la cassa scelta sale e il fondo cassa contabile aumenta dello stesso importo. NON entra nel P&L (non è ricavo).",
    },
    prelievo_capitale: {
        needs_from: true,
        description:
            "Rimborso/restituzione di capitale ai soci: la cassa scelta scende e il fondo cassa contabile diminuisce. NON entra nel P&L (non è costo).",
    },
    restituzione_cliente: {
        needs_client: true,
        needs_from: true,
        description:
            "L'agenzia restituisce contanti/bonifico al cliente (es. dal credito che aveva accumulato): cassa/banca scende, saldo cliente sale. Se il cliente era a credito, il credito si riduce; se andava già a zero, si crea un debito vero.",
    },
    service_adjustment: {
        owner_only: true,
        service_modes: true, // 4 modalità: wallet_in/out + pl_in/out
        description:
            "Rettifica tecnica riservata al proprietario. 4 modalità: due che muovono il saldo del wallet (senza impatto P&L), due che influenzano il P&L (accredito/spesa) senza toccare il wallet — utile quando i soldi sono già nel wallet ma manca la contropartita contabile.",
    },
};

const OPERATOR_ALLOWED_TX_TYPES = new Set([
    "uscita",
    "trasferimento",
    "sospeso",
    "rientro_sospeso",
    "incasso_pos",
    "restituzione_cliente",
]);

export default function Transactions() {
    const { agencyId, user } = useAuth();
    const isOwner = user?.role === "owner";
    const isOperator = user?.role === "operator";
    const [searchParams, setSearchParams] = useSearchParams();
    const [txs, setTxs] = useState([]);
    const [wallets, setWallets] = useState([]);
    const [providers, setProviders] = useState([]);
    const [clients, setClients] = useState([]);
    const [categories, setCategories] = useState([]);
    const [posDevices, setPosDevices] = useState([]);
    const [filterType, setFilterType] = useState("");
    const [filterWallet, setFilterWallet] = useState("");
    const [filterProvider, setFilterProvider] = useState("");
    const [filterClient, setFilterClient] = useState("");
    const [filterStart, setFilterStart] = useState(""); // YYYY-MM-DD
    const [filterEnd, setFilterEnd] = useState("");     // YYYY-MM-DD
    const [open, setOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [form, setForm] = useState({
        type: "sospeso",
        amount: "",
        wallet_from_id: "",
        wallet_to_id: "",
        provider_id: "",
        client_id: "",
        utili_wallet_id: "",
        pos_device_id: "",
        category: "",
        description: "",
        service_direction: "in", // legacy — nuovo campo è service_mode
        service_mode: "wallet_in", // "wallet_in"|"wallet_out"|"pl_in"|"pl_out"
        accredito_kind: "provider", // "provider" | "wallet" (solo per accredito)
        occurred_at: localNowForInput(),
    });

    const cfg = TYPE_CONFIG[form.type];

    const load = () => {
        if (!agencyId) return;
        const params = { agency_id: agencyId, type: filterType || undefined };
        if (filterStart) params.start = new Date(filterStart + "T00:00:00").toISOString();
        if (filterEnd) params.end = new Date(filterEnd + "T23:59:59").toISOString();
        api.get(`/transactions`, { params }).then((r) => setTxs(r.data));
        api.get(`/wallets`, { params: { agency_id: agencyId } }).then((r) =>
            setWallets(r.data),
        );
        api.get(`/providers`, { params: { agency_id: agencyId } }).then((r) =>
            setProviders(r.data),
        );
        api.get(`/clients`, { params: { agency_id: agencyId } }).then((r) =>
            setClients(r.data),
        );
        api.get(`/expense-categories`, { params: { agency_id: agencyId } }).then((r) =>
            setCategories(r.data),
        );
        api.get(`/pos-devices`, { params: { agency_id: agencyId } }).then((r) =>
            setPosDevices(r.data),
        );
    };
    useEffect(() => {
        load();
     
    }, [agencyId, filterType, filterStart, filterEnd]);

    // Auto-open the "new transaction" modal when arriving with ?new=1
    useEffect(() => {
        let cancelled = false;
        if (searchParams.get("new") === "1") {
            // Defer to next tick so it's not synchronous with effect
            const t = setTimeout(() => {
                if (cancelled) return;
                setOpen(true);
                const next = new URLSearchParams(searchParams);
                next.delete("new");
                setSearchParams(next, { replace: true });
            }, 0);
            return () => {
                cancelled = true;
                clearTimeout(t);
            };
        }
        return undefined;
    }, [searchParams, setSearchParams]);

    const cashWallets = useMemo(
        () => wallets.filter((w) => ["cassa", "cassaforte", "banca"].includes(w.type)),
        [wallets],
    );
    const bankWallets = useMemo(
        () => wallets.filter((w) => w.type === "banca"),
        [wallets],
    );
    const utiliWallets = useMemo(
        () => wallets.filter((w) => w.type === "utili_accantonati"),
        [wallets],
    );

    const filteredTxs = useMemo(() => {
        return txs.filter((t) => {
            if (filterWallet && t.wallet_from_id !== filterWallet && t.wallet_to_id !== filterWallet) {
                return false;
            }
            if (filterProvider && t.provider_id !== filterProvider) return false;
            if (filterClient && t.client_id !== filterClient) return false;
            return true;
        });
    }, [txs, filterWallet, filterProvider, filterClient]);

    const activeFiltersCount = [filterType, filterWallet, filterProvider, filterClient, filterStart, filterEnd].filter(Boolean).length;
    const resetFilters = () => {
        setFilterType("");
        setFilterWallet("");
        setFilterProvider("");
        setFilterClient("");
        setFilterStart("");
        setFilterEnd("");
    };

    const applyDatePreset = (preset) => {
        const now = new Date();
        const fmt = (d) => d.toISOString().slice(0, 10);
        if (preset === "all") {
            setFilterStart("");
            setFilterEnd("");
            return;
        }
        const end = fmt(now);
        let start;
        if (preset === "month") {
            const d = new Date(now.getFullYear(), now.getMonth(), 1);
            start = fmt(d);
        } else if (preset === "3months") {
            const d = new Date(now.getFullYear(), now.getMonth() - 3, 1);
            start = fmt(d);
        } else if (preset === "year") {
            const d = new Date(now.getFullYear(), 0, 1);
            start = fmt(d);
        }
        setFilterStart(start);
        setFilterEnd(end);
    };

    const create = async (e) => {
        e.preventDefault();
        try {
            // Auto-create category silently if it's a new name (uscita only)
            if (cfg.needs_category && form.category) {
                const trimmed = form.category.trim();
                const exists = categories.some(
                    (c) => c.name.toLowerCase() === trimmed.toLowerCase(),
                );
                if (!exists) {
                    try {
                        await api.post("/expense-categories", {
                            agency_id: agencyId,
                            name: trimmed,
                        });
                    } catch (err) {
                        // Conflict (already exists race) is fine; other errors fail the tx
                        if (err.response?.status !== 409) throw err;
                    }
                }
            }
            const payload = {
                agency_id: agencyId,
                type: form.type,
                amount: parseFloat(form.amount),
                category: cfg.needs_category ? form.category.trim() : "",
                description: form.description,
            };
            // Convert datetime-local (naive) to ISO with +Z to keep as-is date
            if (form.occurred_at) {
                const dt = new Date(form.occurred_at);
                payload.occurred_at = dt.toISOString();
            }
            if (cfg.needs_from) payload.wallet_from_id = form.wallet_from_id;
            if (cfg.needs_to) payload.wallet_to_id = form.wallet_to_id;
            if (cfg.service_modes) {
                // service_adjustment con 4 modalità
                payload.service_mode = form.service_mode;
                if (form.service_mode === "wallet_in") {
                    if (!form.wallet_to_id) { toast.error("Seleziona un wallet"); return; }
                    payload.wallet_to_id = form.wallet_to_id;
                    payload.wallet_from_id = null;
                } else if (form.service_mode === "wallet_out") {
                    if (!form.wallet_from_id) { toast.error("Seleziona un wallet"); return; }
                    payload.wallet_from_id = form.wallet_from_id;
                    payload.wallet_to_id = null;
                } else {
                    // pl_in / pl_out: nessun wallet
                    payload.wallet_to_id = null;
                    payload.wallet_from_id = null;
                }
            }
            if (cfg.needs_provider || (cfg.needs_provider_optional && form.provider_id))
                payload.provider_id = form.provider_id;
            if (cfg.accredito_target) {
                // XOR fornitore / wallet per accredito
                if (form.accredito_kind === "provider") {
                    if (!form.provider_id) {
                        toast.error("Seleziona un fornitore");
                        return;
                    }
                    payload.provider_id = form.provider_id;
                    payload.wallet_to_id = null;
                } else {
                    if (!form.wallet_to_id) {
                        toast.error("Seleziona un wallet");
                        return;
                    }
                    payload.wallet_to_id = form.wallet_to_id;
                    payload.provider_id = null;
                }
            }
            if (cfg.needs_client) payload.client_id = form.client_id;
            if (cfg.needs_utili) payload.utili_wallet_id = form.utili_wallet_id;
            if (cfg.needs_pos_device) {
                if (!form.pos_device_id) {
                    toast.error("Seleziona quale POS è stato usato");
                    return;
                }
                payload.pos_device_id = form.pos_device_id;
            }
            await api.post("/transactions", payload);
            toast.success("Transazione registrata");
            setOpen(false);
            setForm({
                type: "sospeso",
                amount: "",
                wallet_from_id: "",
                wallet_to_id: "",
                provider_id: "",
                client_id: "",
                utili_wallet_id: "",
                category: "",
                description: "",
                service_direction: "in",
                service_mode: "wallet_in",
                accredito_kind: "provider",
                occurred_at: localNowForInput(),
            });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/transactions/${confirmDelete.id}`);
            toast.success("Transazione eliminata e saldi ripristinati");
            setConfirmDelete(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div data-testid="transactions-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Movimenti</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        Transazioni
                    </h1>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
                    data-testid="add-transaction-btn"
                >
                    <Plus size={14} weight="bold" /> Nuova transazione
                </button>
            </div>

            <div className="bg-white border border-zinc-200 mb-6" data-testid="transactions-filters">
                {/* Periodo: preset + range custom */}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-zinc-200" data-testid="filter-period">
                    <span className="overline text-zinc-500 mr-1">Periodo</span>
                    {[
                        { key: "month", label: "Mese" },
                        { key: "3months", label: "3 mesi" },
                        { key: "year", label: "Anno" },
                        { key: "all", label: "Tutto" },
                    ].map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => applyDatePreset(p.key)}
                            data-testid={`preset-tx-${p.key}`}
                            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-200 bg-white text-zinc-700 hover:border-ink transition-colors"
                        >
                            {p.label}
                        </button>
                    ))}
                    <div className="w-px h-5 bg-zinc-200 mx-1" />
                    <label className="flex items-center gap-1.5 text-xs">
                        <span className="overline">Da</span>
                        <input
                            type="date"
                            value={filterStart}
                            onChange={(e) => setFilterStart(e.target.value)}
                            className="px-2 py-1 text-xs border border-zinc-200 focus:border-ink outline-none"
                            data-testid="filter-start-date"
                        />
                    </label>
                    <label className="flex items-center gap-1.5 text-xs">
                        <span className="overline">A</span>
                        <input
                            type="date"
                            value={filterEnd}
                            onChange={(e) => setFilterEnd(e.target.value)}
                            className="px-2 py-1 text-xs border border-zinc-200 focus:border-ink outline-none"
                            data-testid="filter-end-date"
                        />
                    </label>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
                    <FilterSelect
                        label="Tipo"
                        value={filterType}
                        onChange={setFilterType}
                        options={Object.entries(TX_LABELS)
                            .filter(([k]) => {
                                const c = TYPE_CONFIG[k];
                                if (c?.owner_only && !isOwner) return false;
                                if (c?.manager_only && isOperator) return false;
                                if (isOperator && !OPERATOR_ALLOWED_TX_TYPES.has(k)) return false;
                                return true;
                            })
                            .map(([k, v]) => ({ value: k, label: v }))}
                        placeholder="Tutti i tipi"
                        testid="filter-type-select"
                    />
                    <FilterSelect
                        label="Wallet"
                        value={filterWallet}
                        onChange={setFilterWallet}
                        options={wallets.map((w) => ({ value: w.id, label: w.name }))}
                        placeholder="Tutti i wallet"
                        testid="filter-wallet-select"
                    />
                    <FilterSelect
                        label="Fornitore"
                        value={filterProvider}
                        onChange={setFilterProvider}
                        options={providers.map((p) => ({ value: p.id, label: p.name }))}
                        placeholder="Tutti i fornitori"
                        testid="filter-provider-select"
                    />
                    <FilterSelect
                        label="Cliente"
                        value={filterClient}
                        onChange={setFilterClient}
                        options={clients.map((c) => ({ value: c.id, label: c.name }))}
                        placeholder="Tutti i clienti"
                        testid="filter-client-select"
                    />
                </div>
                {(activeFiltersCount > 0 || txs.length > 0) && (
                    <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-200 bg-zinc-50 text-xs">
                        <div className="text-zinc-600">
                            {activeFiltersCount > 0 ? (
                                <>
                                    <strong className="text-ink">{activeFiltersCount}</strong> filtr{activeFiltersCount === 1 ? "o" : "i"} attiv{activeFiltersCount === 1 ? "o" : "i"} ·{" "}
                                    <strong className="text-ink tabular-nums">{filteredTxs.length}</strong> di {txs.length} transazioni
                                </>
                            ) : (
                                <><strong className="text-ink tabular-nums">{txs.length}</strong> transazioni</>
                            )}
                        </div>
                        {activeFiltersCount > 0 && (
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="inline-flex items-center gap-1 text-zinc-500 hover:text-ink font-bold uppercase tracking-wider"
                                data-testid="reset-filters-btn"
                            >
                                <X size={10} weight="bold" /> Reset
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="ft-table" data-testid="transactions-table">
                    <thead>
                        <tr>
                            <th>Data</th>
                            <th>Tipo</th>
                            <th>Da</th>
                            <th>A</th>
                            <th>Fornitore / Cliente</th>
                            <th>Descrizione</th>
                            <th>Operatore</th>
                            <th className="text-right">Importo</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTxs.map((t) => {
                            const wFrom = wallets.find((w) => w.id === t.wallet_from_id);
                            const wTo = wallets.find((w) => w.id === t.wallet_to_id);
                            const prov = providers.find((p) => p.id === t.provider_id);
                            const client = clients.find((c) => c.id === t.client_id);
                            const sign = [
                                "entrata",
                                "vendita_credito",
                                "commissione",
                                "accredito",
                                "rientro_sospeso",
                                "incasso_pos",
                            ].includes(t.type);
                            return (
                                <tr key={t.id} className="group">
                                    <td className="mono text-xs text-zinc-600">
                                        {shortDate(t.occurred_at)}
                                    </td>
                                    <td>
                                        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase">
                                            {sign ? (
                                                <ArrowUp size={12} className="text-green-600" />
                                            ) : (
                                                <ArrowDown size={12} className="text-red-600" />
                                            )}
                                            {TX_LABELS[t.type]}
                                        </span>
                                    </td>
                                    <td className="text-zinc-600">{wFrom?.name || "—"}</td>
                                    <td className="text-zinc-600">{wTo?.name || "—"}</td>
                                    <td className="text-zinc-600">
                                        {prov && client ? (
                                            <>
                                                <div className="text-xs">{prov.name}</div>
                                                <div className="text-xs text-zinc-500">
                                                    <span className="opacity-60">cliente:</span>{" "}
                                                    <span className="font-bold">{client.name}</span>
                                                </div>
                                            </>
                                        ) : (
                                            prov?.name || client?.name || "—"
                                        )}
                                    </td>
                                    <td className="text-zinc-600">{t.description || "—"}</td>
                                    <td className="text-zinc-500 text-xs">{t.operator_name}</td>
                                    <td className="text-right tabular-nums font-bold">
                                        {euro(t.amount)}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setConfirmDelete(t);
                                            }}
                                            className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 active:bg-zinc-200 transition-colors"
                                            title="Elimina e ripristina saldi"
                                            data-testid={`delete-tx-${t.id}`}
                                            aria-label="Elimina transazione"
                                        >
                                            <Trash size={16} weight="bold" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredTxs.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center text-zinc-500 py-10">
                                    {txs.length === 0
                                        ? "Nessuna transazione"
                                        : "Nessun risultato per i filtri selezionati"}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {open && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setOpen(false)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={create}
                        className="w-full max-w-lg h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="transaction-form"
                    >
                        <div className="overline mb-3">Nuova transazione</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-6">
                            Registra.
                        </h2>

                        <label className="block mb-5">
                            <div className="overline mb-2">Tipo</div>
                            <select
                                className="inp"
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                data-testid="tx-form-type"
                            >
                                {Object.entries(TX_LABELS)
                                    .filter(([k]) => {
                                        const c = TYPE_CONFIG[k];
                                        if (c?.hidden) return false;
                                        if (c?.owner_only && !isOwner) return false;
                                        if (c?.manager_only && isOperator) return false;
                                        if (isOperator && !OPERATOR_ALLOWED_TX_TYPES.has(k)) return false;
                                        return true;
                                    })
                                    .map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                            </select>
                            <div className="text-xs text-zinc-500 mt-1">{cfg.description}</div>
                        </label>

                        {cfg.service_modes && (
                            <label className="block mb-5">
                                <div className="overline mb-2">Modalità rettifica</div>
                                <div className="grid grid-cols-2 gap-2" data-testid="tx-service-modes">
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, service_mode: "wallet_in", wallet_from_id: "" })}
                                        className={
                                            "px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors text-left " +
                                            (form.service_mode === "wallet_in"
                                                ? "bg-ink text-white border-ink"
                                                : "bg-white border-zinc-300 text-zinc-700 hover:border-ink")
                                        }
                                        data-testid="tx-service-mode-wallet-in"
                                    >
                                        + Entrata wallet
                                        <div className="text-[9px] font-normal opacity-70 mt-0.5 normal-case tracking-normal">
                                            Aumenta saldo · no P&L
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, service_mode: "wallet_out", wallet_to_id: "" })}
                                        className={
                                            "px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors text-left " +
                                            (form.service_mode === "wallet_out"
                                                ? "bg-ink text-white border-ink"
                                                : "bg-white border-zinc-300 text-zinc-700 hover:border-ink")
                                        }
                                        data-testid="tx-service-mode-wallet-out"
                                    >
                                        − Uscita wallet
                                        <div className="text-[9px] font-normal opacity-70 mt-0.5 normal-case tracking-normal">
                                            Diminuisce saldo · no P&L
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, service_mode: "pl_in", wallet_to_id: "", wallet_from_id: "" })}
                                        className={
                                            "px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors text-left " +
                                            (form.service_mode === "pl_in"
                                                ? "bg-ink text-white border-ink"
                                                : "bg-white border-zinc-300 text-zinc-700 hover:border-ink")
                                        }
                                        data-testid="tx-service-mode-pl-in"
                                    >
                                        + Accredito (P&L)
                                        <div className="text-[9px] font-normal opacity-70 mt-0.5 normal-case tracking-normal">
                                            Conta come entrata · no wallet
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, service_mode: "pl_out", wallet_to_id: "", wallet_from_id: "" })}
                                        className={
                                            "px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors text-left " +
                                            (form.service_mode === "pl_out"
                                                ? "bg-ink text-white border-ink"
                                                : "bg-white border-zinc-300 text-zinc-700 hover:border-ink")
                                        }
                                        data-testid="tx-service-mode-pl-out"
                                    >
                                        − Spesa (P&L)
                                        <div className="text-[9px] font-normal opacity-70 mt-0.5 normal-case tracking-normal">
                                            Conta come uscita · no wallet
                                        </div>
                                    </button>
                                </div>
                            </label>
                        )}

                        {cfg.accredito_target && (
                            <label className="block mb-5">
                                <div className="overline mb-2">Tipo di accredito</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, accredito_kind: "provider", wallet_to_id: "" })}
                                        className={
                                            "px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors " +
                                            (form.accredito_kind === "provider"
                                                ? "bg-ink text-white border-ink"
                                                : "bg-white border-zinc-300 text-zinc-700 hover:border-ink")
                                        }
                                        data-testid="tx-accredito-kind-provider"
                                    >
                                        Da fornitore
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, accredito_kind: "wallet", provider_id: "" })}
                                        className={
                                            "px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors " +
                                            (form.accredito_kind === "wallet"
                                                ? "bg-ink text-white border-ink"
                                                : "bg-white border-zinc-300 text-zinc-700 hover:border-ink")
                                        }
                                        data-testid="tx-accredito-kind-wallet"
                                    >
                                        Su wallet
                                    </button>
                                </div>
                                <div className="text-xs text-zinc-500 mt-1">
                                    {form.accredito_kind === "provider"
                                        ? "Il fornitore ti riduce lo scoperto: outstanding scende, wallet invariato."
                                        : "Cashback / rimborso / regalo: il wallet aumenta di questo importo."}
                                </div>
                            </label>
                        )}

                        {cfg.accredito_target && form.accredito_kind === "provider" && (
                            <label className="block mb-4">
                                <div className="overline mb-2">Fornitore</div>
                                <select
                                    className="inp"
                                    value={form.provider_id}
                                    onChange={(e) => setForm({ ...form, provider_id: e.target.value })}
                                    required
                                    data-testid="tx-form-accredito-provider"
                                >
                                    <option value="">Seleziona…</option>
                                    {providers.map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {cfg.accredito_target && form.accredito_kind === "wallet" && (
                            <label className="block mb-4">
                                <div className="overline mb-2">Wallet destinatario</div>
                                <select
                                    className="inp"
                                    value={form.wallet_to_id}
                                    onChange={(e) => setForm({ ...form, wallet_to_id: e.target.value })}
                                    required
                                    data-testid="tx-form-accredito-wallet"
                                >
                                    <option value="">Seleziona…</option>
                                    {wallets.map((w) => (
                                        <option key={w.id} value={w.id}>
                                            {w.name} ({euro(w.balance)})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}

                        <label className="block mb-6">
                            <div className="overline mb-2">Importo €</div>
                            <MoneyInput
                                min="0.01"
                                className="inp-huge tabular-nums"
                                value={form.amount}
                                onChange={(v) => setForm({ ...form, amount: v })}
                                required
                                placeholder="0,00"
                                data-testid="tx-form-amount"
                            />
                        </label>

                        <label className="block mb-5">
                            <div className="overline mb-2">Data e ora</div>
                            <input
                                type="datetime-local"
                                className="inp"
                                value={form.occurred_at}
                                onChange={(e) =>
                                    setForm({ ...form, occurred_at: e.target.value })
                                }
                                required
                                data-testid="tx-form-date"
                            />
                            <div className="text-xs text-zinc-500 mt-1">
                                Puoi inserire una data passata per registrare movimenti retroattivi.
                            </div>
                        </label>

                        {cfg.needs_from && (
                            <label className="block mb-4">
                                <div className="overline mb-2">Wallet sorgente</div>
                                <select
                                    className="inp"
                                    value={form.wallet_from_id}
                                    onChange={(e) =>
                                        setForm({ ...form, wallet_from_id: e.target.value })
                                    }
                                    required
                                    data-testid="tx-form-from"
                                >
                                    <option value="">Seleziona…</option>
                                    {wallets.map((w) => {
                                        const label = `${w.name} (${euro(w.balance)})`;
                                        return (
                                            <option key={w.id} value={w.id}>{label}</option>
                                        );
                                    })}
                                </select>
                            </label>
                        )}

                        {cfg.needs_to && (
                            <label className="block mb-4">
                                <div className="overline mb-2">
                                    {form.type === "incasso_pos"
                                        ? "Wallet banca di destinazione"
                                        : form.type === "vendita_credito" ||
                                            form.type === "rientro_sospeso"
                                          ? "Wallet dove entra la cassa"
                                          : "Wallet destinazione"}
                                </div>
                                <select
                                    className="inp"
                                    value={form.wallet_to_id}
                                    onChange={(e) =>
                                        setForm({ ...form, wallet_to_id: e.target.value })
                                    }
                                    required
                                    data-testid="tx-form-to"
                                >
                                    <option value="">Seleziona…</option>
                                    {(form.type === "incasso_pos"
                                        ? bankWallets
                                        : ["vendita_credito", "rientro_sospeso"].includes(form.type)
                                          ? cashWallets
                                          : wallets
                                    ).map((w) => {
                                        const label = `${w.name} (${euro(w.balance)})`;
                                        return (
                                            <option key={w.id} value={w.id}>{label}</option>
                                        );
                                    })}
                                </select>
                                {form.type === "incasso_pos" && bankWallets.length === 0 && (
                                    <div className="text-xs text-red-600 mt-1">
                                        Nessun wallet di tipo &quot;banca&quot;. Creane uno dalla pagina Wallet.
                                    </div>
                                )}
                            </label>
                        )}

                        {cfg.service_modes && (form.service_mode === "wallet_in" || form.service_mode === "wallet_out") && (
                            <label className="block mb-4">
                                <div className="overline mb-2">
                                    {form.service_mode === "wallet_in"
                                        ? "Wallet in cui aggiungere i soldi"
                                        : "Wallet da cui togliere i soldi"}
                                </div>
                                <select
                                    className="inp"
                                    value={form.service_mode === "wallet_in" ? form.wallet_to_id : form.wallet_from_id}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            [form.service_mode === "wallet_in" ? "wallet_to_id" : "wallet_from_id"]: e.target.value,
                                        })
                                    }
                                    required
                                    data-testid="tx-form-service-wallet"
                                >
                                    <option value="">Seleziona…</option>
                                    {wallets.map((w) => (
                                        <option key={w.id} value={w.id}>
                                            {w.name} ({euro(w.balance)})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}

                        {cfg.needs_utili && (
                            <label className="block mb-4">
                                <div className="overline mb-2">Wallet utili accantonati</div>
                                <select
                                    className="inp"
                                    value={form.utili_wallet_id}
                                    onChange={(e) =>
                                        setForm({ ...form, utili_wallet_id: e.target.value })
                                    }
                                    required
                                    data-testid="tx-form-utili"
                                >
                                    <option value="">Seleziona…</option>
                                    {utiliWallets.map((w) => {
                                        const label = `${w.name} (${euro(w.balance)})`;
                                        return (
                                            <option key={w.id} value={w.id}>{label}</option>
                                        );
                                    })}
                                </select>
                                {utiliWallets.length === 0 && (
                                    <div className="text-xs text-red-600 mt-1">
                                        Nessun wallet di tipo &quot;utili accantonati&quot;. Creane uno dalla pagina Wallet.
                                    </div>
                                )}
                            </label>
                        )}

                        {(cfg.needs_provider || cfg.needs_provider_optional) && (
                            <label className="block mb-4">
                                <div className="overline mb-2">
                                    Fornitore {cfg.needs_provider_optional && "(opzionale)"}
                                </div>
                                <select
                                    className="inp"
                                    value={form.provider_id}
                                    onChange={(e) =>
                                        setForm({ ...form, provider_id: e.target.value })
                                    }
                                    required={cfg.needs_provider}
                                    data-testid="tx-form-provider"
                                >
                                    <option value="">Seleziona…</option>
                                    {providers.map((p) => {
                                        const label = `${p.name} (residuo ${euro(p.fido_limit - p.outstanding_credit)})`;
                                        return (
                                            <option key={p.id} value={p.id}>{label}</option>
                                        );
                                    })}
                                </select>
                            </label>
                        )}

                        {cfg.needs_pos_device && (
                            <label className="block mb-4">
                                <div className="overline mb-2">POS utilizzato</div>
                                {posDevices.length === 0 ? (
                                    <div className="p-3 border border-amber-300 bg-amber-50 text-xs text-amber-800">
                                        Nessun POS configurato. Vai su <strong>Impostazioni → Agenzia</strong> per aggiungerne uno.
                                    </div>
                                ) : (
                                    <select
                                        className="inp"
                                        value={form.pos_device_id}
                                        onChange={(e) =>
                                            setForm({ ...form, pos_device_id: e.target.value })
                                        }
                                        required
                                        data-testid="tx-form-pos-device"
                                    >
                                        <option value="">Seleziona POS…</option>
                                        {posDevices.map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.name} (commissione {d.fee_percentage.toFixed(2)}%)
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </label>
                        )}

                        {cfg.needs_client && (
                            <label className="block mb-4">
                                <div className="overline mb-2">Cliente</div>
                                <select
                                    className="inp"
                                    value={form.client_id}
                                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                                    required
                                    data-testid="tx-form-client"
                                >
                                    <option value="">Seleziona…</option>
                                    {clients.map((c) => {
                                        const label = `${c.name} · debito ${euro(c.outstanding_debt)} / fido ${euro(c.fido_limit)}`;
                                        return (
                                            <option key={c.id} value={c.id}>{label}</option>
                                        );
                                    })}
                                </select>
                                {clients.length === 0 && (
                                    <div className="text-xs text-red-600 mt-1">
                                        Nessun cliente. Creane uno dalla pagina Clienti.
                                    </div>
                                )}
                                {(() => {
                                    if (form.type !== "sospeso" || !form.client_id) return null;
                                    const c = clients.find((x) => x.id === form.client_id);
                                    if (!c || !c.fido_limit) return null;
                                    const amt = parseFloat(form.amount) || 0;
                                    const newDebt = (c.outstanding_debt || 0) + amt;
                                    const pct = (newDebt / c.fido_limit) * 100;
                                    if (pct >= 100) {
                                        return (
                                            <div
                                                className="mt-2 p-3 bg-red-50 border border-red-300 text-xs text-red-800"
                                                data-testid="client-fido-over"
                                            >
                                                <strong>OLTRE FIDO</strong> — dopo questo sospeso{" "}
                                                {c.name} avrebbe {euro(newDebt)} di debito su un
                                                fido di {euro(c.fido_limit)} ({pct.toFixed(0)}%).
                                                Valuta se procedere.
                                            </div>
                                        );
                                    }
                                    if (pct >= 80) {
                                        return (
                                            <div
                                                className="mt-2 p-3 bg-amber-50 border border-amber-300 text-xs text-amber-800"
                                                data-testid="client-fido-warning"
                                            >
                                                <strong>Attenzione</strong> — dopo questo sospeso{" "}
                                                {c.name} arriverebbe a {pct.toFixed(0)}% del fido
                                                ({euro(newDebt)}/{euro(c.fido_limit)}).
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="mt-2 text-xs text-zinc-500">
                                            Dopo: {euro(newDebt)}/{euro(c.fido_limit)} (
                                            {pct.toFixed(0)}%)
                                        </div>
                                    );
                                })()}
                            </label>
                        )}

                        {cfg.needs_category && (
                            <label className="block mb-4">
                                <div className="overline mb-2">Categoria</div>
                                <input
                                    list="tx-category-suggestions"
                                    className="inp"
                                    value={form.category}
                                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                                    placeholder={
                                        categories.length > 0
                                            ? "Seleziona o scrivi una nuova categoria"
                                            : "Es: Utenze, Affitto, Stipendi"
                                    }
                                    data-testid="tx-form-category"
                                />
                                <datalist id="tx-category-suggestions">
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.name} />
                                    ))}
                                </datalist>
                                <div className="text-xs text-zinc-500 mt-1">
                                    Se digiti un nome non in elenco, la categoria viene creata automaticamente.
                                </div>
                            </label>
                        )}

                        <label className="block mb-8">
                            <div className="overline mb-2">Descrizione</div>
                            <input
                                className="inp"
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                data-testid="tx-form-description"
                            />
                        </label>

                        <div className="flex gap-2 sticky bottom-0 bg-white pt-4 border-t border-zinc-200">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="tx-form-submit"
                            >
                                Registra
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {confirmDelete && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => !deleting && setConfirmDelete(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="confirm-delete-modal"
                    >
                        <div className="overline mb-3">Eliminare transazione?</div>
                        <h2 className="text-2xl font-black tracking-tighter mb-4">
                            {TX_LABELS[confirmDelete.type]} · {euro(confirmDelete.amount)}
                        </h2>
                        <p className="text-sm text-zinc-700 mb-6">
                            {confirmDelete.description || "Senza descrizione"} ·{" "}
                            {shortDate(confirmDelete.occurred_at)}
                        </p>
                        <div className="mb-6 p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                            I saldi (cassa, fido, debito cliente) verranno{" "}
                            <strong>ripristinati automaticamente</strong>. L&apos;azione non è
                            reversibile.
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmDelete(null)}
                                disabled={deleting}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={remove}
                                disabled={deleting}
                                className="btn-destructive flex-1"
                                data-testid="confirm-delete-button"
                            >
                                {deleting ? "Eliminazione…" : "Elimina"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, placeholder, testid }) {
    return (
        <label className="block border-r border-zinc-200 last:border-r-0 px-3 py-2 hover:bg-zinc-50/50 transition-colors">
            <div className="overline mb-1 text-[10px]">{label}</div>
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={
                        "w-full bg-transparent text-sm font-bold appearance-none cursor-pointer focus:outline-none pr-6 " +
                        (value ? "text-ink" : "text-zinc-500")
                    }
                    data-testid={testid}
                >
                    <option value="">{placeholder}</option>
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">▾</span>
            </div>
        </label>
    );
}
